import { TextInput } from "@a2z/ui";
import { Eye, EyeOff, type LucideIcon } from "lucide-react";
import { useState, type ChangeEventHandler } from "react";
import { useTranslate } from "@/lib/i18n";

/**
 * One labelled input on the login screens: the shared `TextInput` with a
 * leading icon and, for a password, a show/hide eye in its trailing slot.
 * `login.css` gives it the redesign's ring — blue on focus, red while the form
 * carries an error — without a second, local input.
 */
export function AuthField({
  label,
  icon: Icon,
  invalid,
  hint,
  hintTone,
  name,
  type = "text",
  value,
  onChange,
  placeholder,
  required,
  autoComplete,
  inputMode,
  dir,
  testId,
}: {
  label: string;
  icon: LucideIcon;
  invalid?: boolean;
  hint?: string;
  hintTone?: "ok" | "warn" | "error";
  name: string;
  type?: "text" | "tel" | "password";
  value: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  placeholder?: string;
  required?: boolean;
  autoComplete?: string;
  inputMode?: "tel" | "text";
  dir?: "ltr" | "rtl" | "auto";
  testId?: string;
}) {
  const t = useTranslate();
  const [shown, setShown] = useState(false);
  const isPassword = type === "password";
  const eyeLabel = t((m) => (shown ? m.auth.hidePassword : m.auth.showPassword));
  return (
    <label className="auth-field">
      <span className="auth-label">{label}</span>
      <TextInput
        name={name}
        type={isPassword && shown ? "text" : type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        autoComplete={autoComplete}
        inputMode={inputMode}
        dir={dir}
        hasError={invalid}
        data-testid={testId}
        prefix={<Icon size={16} strokeWidth={1.8} aria-hidden />}
        prefixSpace={36}
        suffix={
          isPassword ? (
            <button
              type="button"
              className="auth-eye"
              title={eyeLabel}
              aria-label={eyeLabel}
              aria-pressed={shown}
              onClick={() => setShown((current) => !current)}
            >
              {shown ? (
                <EyeOff size={16} strokeWidth={1.8} aria-hidden />
              ) : (
                <Eye size={16} strokeWidth={1.8} aria-hidden />
              )}
            </button>
          ) : undefined
        }
        suffixSpace={40}
      />
      {hint ? (
        <span className="auth-hint" data-tone={hintTone}>
          {hint}
        </span>
      ) : null}
    </label>
  );
}
